import React from 'react';
import classNames from 'classnames';

import { RCFormGroup } from '../..';
import { FormattedString, CommonInputProps, CustomTagProps } from '../../types';
import { RadioImpl } from '../RCRadioGroup/RadioImpl';
import type { IRCRadioProps } from '../RCRadioGroup/RadioImpl';

export interface RCRadioCardGroupProps extends
  Omit<CommonInputProps, 'id' | 'name' | 'onChange' | 'inputId' | 'large'>,
  Pick<IRCRadioProps, 'iconAlignment'> {
  /** Unique ID passed through the the applicable HTML tags */
  id: string;

  /** Unique ID passed through the the applicable HTML tags */
  name: string;

  /** Passed through to the applicable HTML tag. */
  onChange: React.ChangeEventHandler<HTMLInputElement>;

  /** Current value of the input. Passed through to the `textarea` tag. */
  value: string;

  /** Controls the placement of the radio circle. Defaults to `left`.  */
  iconPlacement?: 'left' | 'right' | 'hidden';

  /** Allows for the label text to be visually hidden, while still providing the proper labeling and context to screen readers.
   * This can be used when a nearby element provides sufficient labelling for sighted users.
   */
  visuallyHiddenLabel?: boolean;
}

type ContextType = Pick<RCRadioCardGroupProps,
  'id' |
  'name' |
  'value' |
  'onChange' |
  'iconAlignment' |
  'required'
> | null;

const RCRadioCardGroupContext = React.createContext<ContextType>(null);

const useRCRadioCardGroupContext = () => React.useContext(RCRadioCardGroupContext);

/**
 * Large clickable radio list. Use this for radio-style selection of large blocks of information, like credit cards or addresses.
 */
export function RCRadioCardGroup({
  errorText,
  helpText,
  id,
  label,
  name,
  onChange,
  tag,
  value,
  iconAlignment = 'top',
  iconPlacement = 'left',
  visuallyHiddenLabel = false,
  required = false,
  children = null,
}: React.PropsWithChildren<RCRadioCardGroupProps>) {
  const checkableGroupID = `rc-radio-card-group--${id}`;

  return (
    <RCRadioCardGroupContext.Provider value={{ onChange, iconAlignment, name, value, id, required }}>
      <RCFormGroup
        errorText={errorText}
        helpText={helpText}
        inputId={id}
        label={label}
        tag={tag}
        labelID={checkableGroupID}
        visuallyHiddenLabel={visuallyHiddenLabel}
        required={required}
      >
        <div
          className={classNames(
            'rc-radio-card-group',
            {[`rc-radio-card-group--icon-${iconPlacement}`]: iconPlacement !== 'left' },
          )}
          role="radiogroup"
          aria-labelledby={checkableGroupID}
        >
          {children}
        </div>
      </RCFormGroup>
    </RCRadioCardGroupContext.Provider>
  );
}


export interface RCRadioCardProps {
  label: FormattedString;
  /** If the sublabel contiains interactive elements, you will need to add a `z-index: 1` to the element so that it sits above the card's `::before` element */
  sublabel?: React.ReactNode;
  value: string;
  disabled?: boolean;
  /** Additional card content below label and sublabel */
  children?: React.ReactNode;
  customTag?: CustomTagProps;
}


function RCRadioCard({
  label,
  sublabel = undefined,
  value,
  disabled = undefined,
  customTag = undefined,
  children = undefined,
}: React.PropsWithChildren<RCRadioCardProps>) {
  const context = useRCRadioCardGroupContext();
  const checked = context?.value === value;
  const cardId = `${context?.id}--${value}`;

  const validChildren = React.Children.toArray(children).filter(Boolean);
  const additionalContentID = validChildren.length > 0 ? `rc-radio-card-content--${cardId}` : undefined;

  return (
    <div className={classNames(
      'rc-radio-card',
      { 'rc-radio-card--disabled': disabled },
      { 'rc-radio-card--checked': checked },
    )}>
      <div className="rc-radio-card__main">
        <RadioImpl
          id={cardId}
          label={label}
          sublabel={sublabel}
          name={context?.name}
          checked={checked}
          value={value}
          onChange={context?.onChange}
          disabled={disabled}
          required={context?.required}
          aria-describedby={additionalContentID}
          iconAlignment={context?.iconAlignment}
          customTag={customTag}
        />
      </div>
      {validChildren.length > 0 && (
        <div
          id={additionalContentID}
          className="rc-radio-card__content"
        >
          {validChildren}
        </div>
      )}
    </div>
  );
}

RCRadioCardGroup.Option = RCRadioCard;
